import React, { useState, useContext, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Modal from "./ui/Modal";
import { swalError } from "../utils/swalAlert";
import { AuthContext } from "../context/AuthContext";
import "../App.css";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function PaymentModal({
  isOpen,
  onClose,
  paymentToken,
  onSuccess,
  customTitle,
  customSubtitle,
}) {
  const { user: authUser, setUser } = useContext(AuthContext) || {};
  const [loading, setLoading] = useState(false);
  const [isSuccessState, setIsSuccessState] = useState(false);
  const [extendedExpiryDate, setExtendedExpiryDate] = useState(null);

  const actualPrice = parseInt(process.env.REACT_APP_ACTUAL_PRICE || "799", 10);
  const discountedPrice = parseInt(
    process.env.REACT_APP_DISCOUNTED_PRICES || "399",
    10
  );
  const discountPercent = Math.round(
    ((actualPrice - discountedPrice) / actualPrice) * 100
  );

  // Compute expiry details
  const expiryInfo = useMemo(() => {
    const rawExpiry = authUser?.loginExpiryDate;
    if (!rawExpiry) return { isExpired: true, daysRemaining: 0, formattedDate: "" };

    const expDate = new Date(rawExpiry);
    const now = new Date();
    const diffTime = expDate.getTime() - now.getTime();
    const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const formattedDate = expDate.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    return {
      isExpired: daysRemaining <= 0,
      daysRemaining,
      formattedDate,
    };
  }, [authUser?.loginExpiryDate]);

  const handleProceedPayment = async () => {
    try {
      setLoading(true);

      const loaded = await loadRazorpay();
      if (!loaded) {
        throw new Error("Unable to load Razorpay checkout SDK.");
      }

      const response = await fetch(`${serverUrl}/api/payments/create-order`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(paymentToken ? { "X-Payment-Token": paymentToken } : {}),
        },
        body: JSON.stringify({ amount: discountedPrice }),
      });

      const order = await response.json();
      if (!response.ok) {
        throw order;
      }

      const razorpay = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.orderId,
        name: "GradeMe AI",
        description: "1-year plan extension for JEE / NEET Prep",
        handler: async (paymentResponse) => {
          try {
            const verifyRes = await fetch(
              `${serverUrl}/api/payments/verify-payment`,
              {
                method: "POST",
                credentials: "include",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  ...(paymentToken ? { "X-Payment-Token": paymentToken } : {}),
                },
                body: JSON.stringify(paymentResponse),
              }
            );

            const result = await verifyRes.json();
            if (!verifyRes.ok) {
              throw result;
            }

            if (result.success) {
              // Refresh user data if user is already signed in
              if (setUser) {
                try {
                  const userRes = await fetch(`${serverUrl}/api/user/signed-in`, {
                    credentials: "include",
                    headers: { Accept: "application/json" },
                  });
                  if (userRes.ok) {
                    const freshUserData = await userRes.json();
                    if (freshUserData && freshUserData._id) {
                      const category =
                        freshUserData.category ||
                        freshUserData.institute?.category ||
                        "K12_SCHOOL";
                      setUser({ ...freshUserData, category });
                      if (freshUserData.loginExpiryDate) {
                        const newExp = new Date(freshUserData.loginExpiryDate);
                        setExtendedExpiryDate(
                          newExp.toLocaleDateString("en-US", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        );
                      }
                    }
                  }
                } catch (e) {
                  console.error("Error refreshing user context post-payment:", e);
                }
                setIsSuccessState(true);
              }

              onSuccess?.(result);
            } else {
              swalError(result.message || "Payment verification failed.");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            swalError(
              error.message ||
                "Payment verification failed. Please contact support if money was deducted."
            );
          } finally {
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
          },
        },
        theme: {
          color: "#0f172a",
        },
      });

      razorpay.on("payment.failed", (res) => {
        console.error("Razorpay payment failed:", res.error);
        swalError(res.error?.description || "Payment failed. Please try again.");
        setLoading(false);
      });

      razorpay.open();
    } catch (error) {
      console.error("Payment error:", error);
      swalError(error.message || "Unable to start payment. Please try again.");
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    if (loading) return;
    setIsSuccessState(false);
    onClose?.();
  };

  const firstName = authUser?.firstName || "";

  const benefits = [
    {
      icon: "fa-solid fa-bullseye",
      color: "pp-icon-c1",
      title: "Stop Guessing, Start Knowing",
      desc: "Practice with questions that mirror your actual exam. No surprises on exam-day.",
    },
    {
      icon: "fa-solid fa-brain",
      color: "pp-icon-c2",
      title: "Your Weak Spots Won't Hide Anymore",
      desc: "AI finds exactly where you lose marks, so you fix it before the exam does.",
    },
    {
      icon: "fa-solid fa-infinity",
      color: "pp-icon-c3",
      title: "Turn Exam Anxiety Into Confidence",
      desc: "Take as many mock tests as you need until the exam feels familiar, not frightening.",
    },
    {
      icon: "fa-solid fa-shield-halved",
      color: "pp-icon-c4",
      title: "365 Days. One Dream. Zero Compromise.",
      desc: "Uninterrupted preparation from today until the exam hall. No gaps, no excuses.",
    },
  ];

  // Dynamic Header Copy
  const headerTitle = `${firstName ? `${firstName}, your` : "Your"} preparation shouldn't stop here`

  const headerSubtitle = "Every day of practice brings you closer to the rank you deserve. Don't let a gap in access become a gap in your preparation."

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      maxWidth="60dvw"
      modalContentClass="pp-modal-content"
    >
      <AnimatePresence mode="wait">
        {isSuccessState ? (
          /* Celebratory Success View */
          <motion.div
            key="success-view"
            className="pp-success-container"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3 }}
          >
            <div className="pp-success-icon-wrapper">
              <motion.div
                className="pp-success-icon"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
              >
                <i className="fa-solid fa-circle-check"></i>
              </motion.div>
              <div className="pp-confetti-sparkle s1">✨</div>
              <div className="pp-confetti-sparkle s2">🎉</div>
              <div className="pp-confetti-sparkle s3">⭐</div>
            </div>

            <h2 className="pp-success-title">Subscription Renewed Successfully!</h2>
            <p className="pp-success-message">
              Thank you! Your GradeMe AI account has been successfully extended for{" "}
              <strong>1 full year (365 Days)</strong>.
              {extendedExpiryDate && (
                <span className="pp-success-date">
                  <br />
                  Valid through: <strong>{extendedExpiryDate}</strong>
                </span>
              )}
            </p>

            <div className="pp-success-perks-row">
              <span className="pp-success-perk">
                <i className="fa-solid fa-check"></i> Unlimited JEE/NEET Tests
              </span>
              <span className="pp-success-perk">
                <i className="fa-solid fa-check"></i> AI Analytics Unlocked
              </span>
              <span className="pp-success-perk">
                <i className="fa-solid fa-check"></i> Full Verified Solutions
              </span>
            </div>

            <motion.button
              className="pp-cta-primary pp-success-btn"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleModalClose}
            >
              <span>Continue Preparing</span>
              <i className="fa-solid fa-arrow-right"></i>
            </motion.button>
          </motion.div>
        ) : (
          /* Standard Payment & Value Proposition View */
          <div className="pp-container" key="payment-view">
            {/* Left Side / Information Column */}
            <div className="pp-main-col">
              {/* Header */}
              <motion.div
                className="pp-header"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: 0.05 }}
              >
                <div className="pp-header-badges">
                  {expiryInfo.daysRemaining > 0 ? (
                    <span className="pp-expiring-tag">
                      <i className="fa-solid fa-hourglass-half"></i> Access Ends in{" "}
                      {expiryInfo.daysRemaining}{" "}
                      {expiryInfo.daysRemaining === 1 ? "Day" : "Days"}
                    </span>
                  ) : (
                    <span className="pp-expired-tag">
                      <i className="fa-solid fa-clock-rotate-left"></i> Subscription Expired
                    </span>
                  )}
                  <span className="pp-topper-badge">
                    <i className="fa-solid fa-medal"></i> Trusted by Toppers
                  </span>
                </div>
                <h2 className="pp-title">{headerTitle}</h2>
                <p className="pp-subtitle">{headerSubtitle}</p>
              </motion.div>

              {/* Benefits Grid (2x2) */}
              <motion.div
                className="pp-benefits-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.35, delay: 0.15 }}
              >
                {benefits.map((b, i) => (
                  <motion.div
                    key={i}
                    className="pp-benefit-card"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: 0.2 + i * 0.05 }}
                  >
                    <div className={`pp-benefit-icon ${b.color}`}>
                      <i className={b.icon}></i>
                    </div>
                    <div className="pp-benefit-text">
                      <strong>{b.title}</strong>
                      <span>{b.desc}</span>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Right Side / Pricing & Action Sidebar */}
            <div className="pp-sidebar-col">
              <motion.div
                className="pp-pricing-card"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: 0.25 }}
              >
                <div className="pp-pricing-top-row">
                  <div className="pp-plan-info">
                    <span className="pp-plan-label">Annual Plan</span>
                    <span className="pp-plan-duration">365 Days Extension</span>
                  </div>
                  <div className="pp-save-badge">
                    <i className="fa-solid fa-tags"></i> Save {discountPercent}%
                  </div>
                </div>

                <div className="pp-price-row">
                  <span className="pp-price-original">₹{actualPrice}</span>
                  <span className="pp-price-final">₹{discountedPrice}</span>
                  <span className="pp-price-period">/year</span>
                </div>

                <div className="pp-price-perks">
                  <span>
                    <i className="fa-solid fa-circle-check"></i> Instant activation
                  </span>
                  <span>
                    <i className="fa-solid fa-circle-check"></i> Access to all JEE / NEET Tests
                  </span>
                  <span>
                    <i className="fa-solid fa-circle-check"></i> Detailed AI Analytics
                  </span>
                  <span>
                    <i className="fa-solid fa-circle-check"></i> 100% Secure payment
                  </span>
                </div>

                <div className="pp-actions">
                  <motion.button
                    className="pp-cta-primary"
                    whileHover={!loading ? { scale: 1.02, y: -1 } : {}}
                    whileTap={!loading ? { scale: 0.98 } : {}}
                    disabled={loading}
                    onClick={handleProceedPayment}
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-circle-notch fa-spin"></i>
                        <span>Opening Payment Gateway...</span>
                      </>
                    ) : (
                      <>
                        <span>Continue My Preparation</span>
                        <span className="pp-cta-price">₹{discountedPrice}</span>
                      </>
                    )}
                  </motion.button>

                  <button
                    className="pp-cta-secondary"
                    type="button"
                    disabled={loading}
                    onClick={handleModalClose}
                  >
                    Maybe Later
                  </button>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </AnimatePresence>
    </Modal>
  );
}